/**
 * FHIR constants, CodeSystems, and helpers for Service Requests (client service requests).
 *
 * Mirrors `docs/PRD_SERVICE_REQUESTS_FHIR_MAPPING.md`. All literals for tracker stage,
 * category, therapy service type, and the episode-of-care extension live here so the
 * rest of the app can reference strongly typed constants instead of inline strings.
 */

import type {
  CodeableConcept,
  Coding,
  Extension,
  Meta,
  ServiceRequest,
} from '@medplum/fhirtypes';

// ---------------------------------------------------------------------------
// System URIs
// ---------------------------------------------------------------------------

/** Tag system for the tracker stage (single source of truth for pipeline status). */
export const TRACKER_STAGE_TAG_SYSTEM = 'https://unify.health/fhir/tracker-stage';

/** CodeSystem for `ServiceRequest.category` that distinguishes from Health Gorilla lab orders. */
export const SERVICE_REQUEST_CATEGORY_SYSTEM =
  'https://unify.health/fhir/CodeSystem/service-request-category';

/** Authoritative CodeSystem for therapy service types as referenced in the PRD. */
export const THERAPY_SERVICE_TYPE_CS_SYSTEM =
  'https://unify.health/fhir/CodeSystem/therapy-service-type';

/**
 * Legacy therapy-service-type system kept for backward compatibility with the existing
 * caseload seed/model. Lookups accept both. All *new* writes use the CodeSystem URL above.
 */
export const THERAPY_SERVICE_TYPE_LEGACY_SYSTEM =
  'https://unify.health/fhir/therapy-service-type';

/** StructureDefinition URL for the 1:1 `ServiceRequest` → `EpisodeOfCare` link. */
export const SERVICE_REQUEST_EOC_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/episode-of-care';

// ---------------------------------------------------------------------------
// Tracker stage codes (single source of truth; maps 1:1 to tracker tabs)
// ---------------------------------------------------------------------------

export const TRACKER_STAGE_CODES = [
  'new-client',
  'needs-verification',
  'waitlist',
  'screening',
  'matching',
  'consultation',
  'fulfilled',
  'inactive',
  'cancelled',
  'transfer',
] as const;

export type TrackerStageCode = (typeof TRACKER_STAGE_CODES)[number];

/** Display labels for tracker stages — mirror the Service Request Tracker tab labels. */
export const TRACKER_STAGE_DISPLAY: Record<TrackerStageCode, string> = {
  'new-client': 'Incomplete',
  'needs-verification': 'Eligibility',
  waitlist: 'Waitlist',
  screening: 'Screening',
  matching: 'Matching',
  consultation: 'Consultation',
  fulfilled: 'Fulfilled',
  inactive: 'Inactive',
  cancelled: 'Cancelled',
  transfer: 'Transfer',
};

export function isTrackerStageCode(v: unknown): v is TrackerStageCode {
  return (
    typeof v === 'string' && (TRACKER_STAGE_CODES as readonly string[]).includes(v)
  );
}

// ---------------------------------------------------------------------------
// Service request category
// ---------------------------------------------------------------------------

export const SERVICE_REQUEST_CATEGORY_CODE = 'service-request';

export const SERVICE_REQUEST_CATEGORY: CodeableConcept = {
  coding: [
    {
      system: SERVICE_REQUEST_CATEGORY_SYSTEM,
      code: SERVICE_REQUEST_CATEGORY_CODE,
      display: 'Service Request',
    },
  ],
};

/** Medplum search token for `category`. */
export const SERVICE_REQUEST_CATEGORY_SEARCH_TOKEN = `${SERVICE_REQUEST_CATEGORY_SYSTEM}|${SERVICE_REQUEST_CATEGORY_CODE}`;

/** True when the resource is a Unify client service request (not a lab order, etc.). */
export function hasServiceRequestCategory(
  sr: Pick<ServiceRequest, 'category'>,
): boolean {
  return (sr.category ?? []).some((cc) =>
    (cc.coding ?? []).some(
      (c) =>
        c.system === SERVICE_REQUEST_CATEGORY_SYSTEM &&
        c.code === SERVICE_REQUEST_CATEGORY_CODE,
    ),
  );
}

// ---------------------------------------------------------------------------
// Therapy service types (ServiceRequest.code)
// ---------------------------------------------------------------------------

export const THERAPY_SERVICE_TYPE_DISPLAY: Record<string, string> = {
  individual: 'Individual Therapy',
  couples: 'Couples Therapy',
  family: 'Family Therapy',
  group: 'Group Therapy',
  'group-therapy': 'Group Therapy',
  'child-adolescent': 'Child & Adolescent Therapy',
  iop: 'Intensive Outpatient Program',
  'medication-management': 'Medication Management',
};

export function therapyServiceTypeCoding(code: string): Coding {
  return {
    system: THERAPY_SERVICE_TYPE_CS_SYSTEM,
    code,
    display: THERAPY_SERVICE_TYPE_DISPLAY[code] ?? code,
  };
}

export function therapyServiceTypeConcept(code: string): CodeableConcept {
  const coding = therapyServiceTypeCoding(code);
  return { coding: [coding], text: coding.display };
}

// ---------------------------------------------------------------------------
// Meta.tag helpers for tracker stage
// ---------------------------------------------------------------------------

/** Build a `meta.tag` Coding for the given tracker stage. */
export function buildTrackerStageTag(code: TrackerStageCode): Coding {
  return {
    system: TRACKER_STAGE_TAG_SYSTEM,
    code,
    display: TRACKER_STAGE_DISPLAY[code],
  };
}

/** Read the tracker stage from a resource's `meta.tag` array. */
export function readTrackerStage(
  meta: Meta | undefined,
): TrackerStageCode | undefined {
  const tag = meta?.tag?.find((t) => t.system === TRACKER_STAGE_TAG_SYSTEM);
  return isTrackerStageCode(tag?.code) ? (tag!.code as TrackerStageCode) : undefined;
}

/**
 * Return a new `meta` object where any existing tracker-stage tag is replaced by `stage`.
 * Leaves non-tracker tags intact so pilot/seed tags are preserved.
 */
export function upsertTrackerStageTag(
  meta: Meta | undefined,
  stage: TrackerStageCode,
): Meta {
  const nextTags = (meta?.tag ?? []).filter(
    (t) => t.system !== TRACKER_STAGE_TAG_SYSTEM,
  );
  nextTags.push(buildTrackerStageTag(stage));
  return { ...(meta ?? {}), tag: nextTags };
}

/** Build the Medplum `_tag` search token for a tracker stage. */
export function trackerStageSearchToken(stage: TrackerStageCode): string {
  return `${TRACKER_STAGE_TAG_SYSTEM}|${stage}`;
}

// ---------------------------------------------------------------------------
// Episode-of-care extension helpers
// ---------------------------------------------------------------------------

export function buildEpisodeOfCareExtension(
  episodeOfCareId: string,
  display?: string,
): Extension {
  return {
    url: SERVICE_REQUEST_EOC_EXTENSION,
    valueReference: {
      reference: `EpisodeOfCare/${episodeOfCareId}`,
      ...(display ? { display } : {}),
    },
  };
}

export function readEpisodeOfCareReference(
  sr: Pick<ServiceRequest, 'extension'>,
): string | undefined {
  const ext = sr.extension?.find((e) => e.url === SERVICE_REQUEST_EOC_EXTENSION);
  return ext?.valueReference?.reference ?? undefined;
}

/** Returns the raw `{id}` portion of an `EpisodeOfCare/{id}` reference if present. */
export function readEpisodeOfCareId(
  sr: Pick<ServiceRequest, 'extension'>,
): string | undefined {
  const ref = readEpisodeOfCareReference(sr);
  if (!ref) return undefined;
  const prefix = 'EpisodeOfCare/';
  return ref.startsWith(prefix) ? ref.slice(prefix.length) : undefined;
}

// ---------------------------------------------------------------------------
// Derived FHIR status (mechanical — app logic must not depend on this)
// ---------------------------------------------------------------------------

/**
 * All service requests carry FHIR `status: 'active'` per the PRD. Expressed as a
 * helper so tests can reference it as a constant.
 */
export const SERVICE_REQUEST_STATIC_STATUS = 'active' as const;

/** Fixed `ServiceRequest.intent` per the PRD. */
export const SERVICE_REQUEST_STATIC_INTENT = 'order' as const;
